import type { AgentDto, ProviderDto } from '@af/shared';

/** Provider type for an agent's current model (`<providerSlug>:<model>`). */
export function providerTypeFor(
  agent: AgentDto | undefined,
  providers: ProviderDto[] | undefined,
): string | null {
  const ref = agent?.currentVersion?.modelRef;
  if (!ref) return null;
  const slug = ref.slice(0, ref.indexOf(':'));
  return (
    providers?.find((p) => p.slug === slug)?.type ?? (slug === 'anthropic' || slug === 'openai' ? slug : null)
  );
}

/** The model part of a `provider:model` ref. */
export const refModelName = (ref: string): string => ref.slice(ref.indexOf(':') + 1);

export function modelName(agent: AgentDto | undefined): string {
  return refModelName(agent?.currentVersion?.modelRef ?? '') || 'no model';
}

export function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join('') || '?'
  );
}

/** Pixel-avatar color by provider. */
export function providerColor(type: string | null): string {
  if (type === 'anthropic') return 'var(--claude)';
  if (type?.startsWith('openai')) return 'var(--success)';
  return 'var(--accent)';
}

/** The top-level manager agent (no manager, most direct reports) — the default chat target. */
export function topManager(agents: AgentDto[] | undefined): AgentDto | undefined {
  const live = (agents ?? []).filter((a) => a.status === 'active');
  if (!live.length) return undefined;
  const ids = new Set((agents ?? []).map((a) => a.id));
  const reports = (id: string) => (agents ?? []).filter((a) => a.managerAgentId === id).length;
  const roots = live.filter((a) => !a.managerAgentId || !ids.has(a.managerAgentId));
  return [...(roots.length ? roots : live)].sort((a, b) => reports(b.id) - reports(a.id))[0];
}

const RUNTIME_LABEL: Record<string, string> = {
  'ai-sdk': 'AI SDK',
  'claude-agent-sdk': 'Claude SDK',
  'codex-sdk': 'Codex SDK',
};

/** "opus-4-5 · Claude SDK" style chip label for an agent's model + runtime. */
export function modelRuntimeLabel(agent: AgentDto | undefined): string {
  const model = modelName(agent).replace(/^claude-/, '');
  const runtime = RUNTIME_LABEL[agent?.currentVersion?.runtime ?? 'ai-sdk'] ?? agent?.currentVersion?.runtime;
  return `${model} · ${runtime}`;
}

/** Stable hue (0–359) for an agent's gradient orb. */
export function agentHue(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return h;
}
